/**
 * Geolocation utilities for determining user country and filtering products
 */

// Country code mapping for API compatibility
const COUNTRY_CODE_MAP = {
    'MX': 'mx',      // 🇲🇽 México
    'US': 'us',      // 🇺🇸 Estados Unidos
    'CA': 'ca',      // 🇨🇦 Canadá
    'ES': 'es',      // 🇪🇸 España
    'AR': 'ar',      // 🇦🇷 Argentina
    'CO': 'co',      // 🇨🇴 Colombia
    'CL': 'cl',      // 🇨🇱 Chile
    'PE': 'pe',      // 🇵🇪 Perú
    'BR': 'br',      // 🇧🇷 Brasil
    'GB': 'global',  // United Kingdom -> Global
    'FR': 'global',  // France -> Global
    'DE': 'global',  // Germany -> Global
    'IT': 'global',  // Italy -> Global
    'default': 'global' // Default for unknown countries
};

/**
 * Get user's country using IP-based geolocation
 * Falls back to browser geolocation if IP method fails
 */
export const getUserCountry = async () => {
    try {
        // Method 1: IP-based geolocation (most reliable)
        const ipCountry = await getCountryFromIP();
        if (ipCountry) {
            return ipCountry;
        }

        // Method 2: Browser geolocation fallback
        const geoCountry = await getCountryFromGeolocation();
        if (geoCountry) {
            return geoCountry;
        }

        // Method 3: Default fallback
        return 'global';
    } catch (error) {
        console.warn('Failed to detect user country:', error);
        return 'global';
    }
};

/**
 * Get country from IP address using free IP geolocation service
 */
const getCountryFromIP = async () => {
    try {
        const response = await fetch('https://ipapi.co/json/', {
            timeout: 5000
        });

        if (!response.ok) {
            throw new Error('IP geolocation service unavailable');
        }

        const data = await response.json();
        const countryCode = data.country_code?.toUpperCase();

        if (countryCode && COUNTRY_CODE_MAP[countryCode]) {
            console.log(`User detected in ${data.country_name} (${countryCode}) -> API code: ${COUNTRY_CODE_MAP[countryCode]}`);
            return COUNTRY_CODE_MAP[countryCode];
        }

        return null;
    } catch (error) {
        console.warn('IP geolocation failed:', error);
        return null;
    }
};

/**
 * Get country from browser geolocation (requires user permission)
 */
const getCountryFromGeolocation = async () => {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve(null);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const { latitude, longitude } = position.coords;
                    const country = await reverseGeocode(latitude, longitude);
                    resolve(country);
                } catch (error) {
                    console.warn('Reverse geocoding failed:', error);
                    resolve(null);
                }
            },
            (error) => {
                console.warn('Browser geolocation failed:', error);
                resolve(null);
            },
            {
                enableHighAccuracy: false,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes cache
            }
        );
    });
};

/**
 * Reverse geocoding to get country from coordinates
 */
const reverseGeocode = async (lat, lng) => {
    try {
        // Using OpenStreetMap Nominatim (free service)
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
            {
                headers: {
                    'User-Agent': 'LaRelojeriaIR/1.0'
                }
            }
        );

        if (!response.ok) {
            throw new Error('Reverse geocoding service unavailable');
        }

        const data = await response.json();
        const countryCode = data.address?.country_code?.toUpperCase();

        if (countryCode && COUNTRY_CODE_MAP[countryCode]) {
            return COUNTRY_CODE_MAP[countryCode];
        }

        return null;
    } catch (error) {
        console.warn('Reverse geocoding failed:', error);
        return null;
    }
};

/**
 * Get the appropriate country code for API filtering
 * @param {string} country - Country code (mx, us, global)
 * @returns {string} - Country code for API
 */
export const getApiCountryCode = (country) => {
    return country || 'global';
};

/**
 * Build product API query with country filter
 * @param {string} baseUrl - Base API URL
 * @param {string} country - Country code for filtering
 * @param {object} additionalParams - Additional query parameters
 * @returns {string} - Complete API URL with filters
 */
export const buildProductApiUrl = (baseUrl, country, additionalParams = {}) => {
    const url = new URL(baseUrl);

    // Build query string manually to avoid double encoding of where[] parameters
    const queryParams = [];

    // Add country filter - just send the country as-is
    if (country && country !== 'global') {
        queryParams.push(`where[disponibilidadPais][equals]=${country}`);
    } else {
        queryParams.push(`where[disponibilidadPais][equals]=global`);
    }

    // Add additional parameters - handle where[] parameters specially
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            if (key.startsWith('where[')) {
                // For where[] parameters, add them manually to avoid encoding
                queryParams.push(`${key}=${value}`);
            } else {
                // For regular parameters, use URLSearchParams
                url.searchParams.append(key, value);
            }
        }
    });

    // Add the manually constructed query parameters
    if (queryParams.length > 0) {
        url.search += (url.search ? '&' : '') + queryParams.join('&');
    }

    return url.toString();
};
